import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { AuthenticationService } from './_authentication.service';

@Injectable({
  providedIn: 'root',
})
export class RouteNavigationService {
  public isRouting = false;
  private ngOnDestroy$ = new Subject<void>();
  private navHistory: string[] = [];

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private location: Location
  ) {
    this.router.events.pipe(takeUntil(this.ngOnDestroy$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.navHistory.push(event.urlAfterRedirects);
      }
    });

    this.router.events
      .pipe(takeUntil(this.ngOnDestroy$))
      .pipe(
        filter(
          (
            event:
              | NavigationStart
              | NavigationEnd
              | NavigationCancel
              | NavigationError
          ) =>
            event instanceof NavigationStart ||
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        )
      )
      .subscribe(
        async (
          event:
            | NavigationStart
            | NavigationEnd
            | NavigationCancel
            | NavigationError
        ) => {
          this.isRouting = event instanceof NavigationStart;
          if (event instanceof NavigationEnd) {
            this.navHistory.push(event.urlAfterRedirects);
          }
        }
      );
  }

  get currentSubDomain(): string {
    const t = this.authService.currentTenantValue;
    if (t) {
      return t.subDomainName;
    }
    return 'home';
  }

  public login(returnUrl: string = null) {
    this.navigate('login', returnUrl);
  }

  public navigateBack() {
    this.navHistory.pop();
    if (this.navHistory.length > 0) {
      this.location.back();
    } else {
      // Go to dashboard if no location history
      this.navigate('admin/dashboard');
    }
  }

  public navigateWithUrlSegment(
    url: string,
    segment: any,
    returnUrl: string = null
  ) {
    if (returnUrl !== null) {
      this.router.navigate([this.prependSubDomain(url), segment], {
        queryParams: { returnUrl },
      });
      return;
    }
    this.router.navigate([this.prependSubDomain(url), segment]);
  }

  public explicitNavigate(route: string, returnUrl: string = null) {
    if (returnUrl !== null) {
      this.router.navigate([route], {
        queryParams: { returnUrl },
      });
      return;
    }
    this.router.navigate([route]);
  }

  public navigate(url: string, returnUrl: string = null): void {
    if (returnUrl !== null) {
      this.router.navigate([this.prependSubDomain(url)], {
        queryParams: { returnUrl },
      });
      return;
    }
    this.router.navigate([this.prependSubDomain(url)]);
  }

  public goToHome() {
    this.router.navigate(['home']);
  }

  public tenantAuthPage(returnUrl: string = null) {
    if (returnUrl !== null) {
      this.router.navigate([this.currentSubDomain], {
        queryParams: { returnUrl },
      });
      return;
    }
    this.router.navigate([this.currentSubDomain]);
  }

  public redirect(url: string, returnUrl: string = null) {
    if (returnUrl !== null) {
      this.router.navigate([this.prependSubDomain(url)], {
        queryParams: { returnUrl },
      });
      return;
    }
    this.navigate(this.prependSubDomain(url));
  }

  private prependSubDomain(url: string): string {
    return `${this.currentSubDomain}/${url}`;
  }
}
